<script lang="ts">
	import { DropdownMenu, MenuItemList, IconButton } from '@Shelly/shelly-design-system';
	import DashWidgetActionModal from 'src/components/AddActions/Wizards/DashWidgetActionModal.svelte';
	import { mapDashWidgetToEditItem } from 'src/components/Dashboards/dash-helpers';
	import type { MenuItemListExtendedProps } from 'src/components/Dashboards/dash-types';
	import DashDeleteWidgetModal from 'src/components/Dashboards/DashDeleteWidgetModal/DashDeleteWidgetModal.svelte';
	import DashReorderWidgetItemsModal from 'src/components/Dashboards/DashReorderWidgetItemsModal/DashReorderWidgetItemsModal.svelte';
	import OptionManagmentModal from 'src/components/OptionManagmentModal.svelte';
	import { mapIconSolidWeightToLight } from 'src/helpers/new-layout-helpers';
	import { is_mobile, is_tablet } from 'src/stores/ui/viewport';
	import { edit } from 'src/stores/ui/edit_lists';
	import { format } from 'svelte-i18n';
	import { location } from 'svelte-spa-router';

	export let editItem;
	export let dashboardId: string | number | undefined;
	export let energyOnly = false;

	const WIDGET_TYPES_NO_REORDABLE_ITEMS = [
		'dashWeather',
		'dashChart',
		'dashInfo',
		'dashEnergyInfo',
		'dashEnergyKpiLive',
		'dashEnergyKpiPeriod',
		'dashEnergyTopConsumers',
		'dashEnergyConsumerMix',
		'dashEnergyRoomChart',
		'dashEnergyFlow',
		'dashEnergyHeatmap',
		'dashEnergyPeak',
		'dashEnergyInsights',
		'dashEnergyGeneration',
		'dashEnergyPvDistribution',
		'dashEnergyTariff',
		'dashEnergyPhase',
		'dashEnergyEnvironmental',
		'dashEnergyDeviceTrend',
		'dashEnergyRoomCharts',
		'dashEnergyRoomDistribution',
	];

	const hideReorderMenu = WIDGET_TYPES_NO_REORDABLE_ITEMS.includes(editItem.type);

	const manageWidgetGroup = {
		title: $format('label_manage_widget', {
			default: 'Manage Widget',
		}),
		isGroupTitle: true,
		items: [
			{
				title: $format('label_edit_widget', {
					default: 'Edit widget',
				}),
				icon: 'fas fa-pen-to-square',
				onClick: () => {
					showEditWidgetModal = true;
					toggleWidgetMenu();
				},
			},
			{
				title: $format('label_delete_widget', {
					default: 'Delete widget',
				}),
				titleClasses: 'text-danger',
				icon: 'fas fa-trash text-danger',
				onClick: () => {
					showDeleteModal = true;
					toggleWidgetMenu();
				},
			},
		],
	};

	const reorderWigetItemsGroup = {
		title: 'Items',
		isGroupTitle: true,
		items: [
			{
				title: $format('prompt_title_reorder_widget_items', {
					default: 'Reorder widget items',
				}),
				icon: 'fas fa-arrows-up-down-left-right',
				onClick: () => {
					showItemReorderModal = true;
					toggleWidgetMenu();
				},
			},
		],
	};

	const dropdownMenuItemsData: MenuItemListExtendedProps[] = hideReorderMenu
		? [manageWidgetGroup]
		: [manageWidgetGroup, reorderWigetItemsGroup];

	let showDropdownMenu = false;
	let showModalMenu = false;
	let showEditWidgetModal = false;
	let showDeleteModal = false;
	let showItemReorderModal = false;

	const toggleWidgetMenu = () => {
		if ($is_tablet || $is_mobile) {
			showModalMenu = !showModalMenu;
		} else if (!$is_tablet && !$is_mobile) {
			showDropdownMenu = !showDropdownMenu;
		}
	};

	$: dashWidgetItemEdit = mapDashWidgetToEditItem(editItem, dashboardId);

	$: locationSplit = $location.split('/').filter(Boolean);

	$: routeParams = {
		id: locationSplit[1] === 'room' ? locationSplit[2] : '-1',
		type: locationSplit[0] === 'dash' ? locationSplit[0] : locationSplit[1],
	};
</script>

<DropdownMenu bind:show={showDropdownMenu}>
	<IconButton
		slot="trigger"
		aria-label="Dash Widget More Action"
		shape="circle"
		variant="secondary"
		icon="fas fa-ellipsis-vertical"
		on:click={toggleWidgetMenu}
	/>

	{#each dropdownMenuItemsData as groupItem}
		<MenuItemList isGroupTitle={groupItem?.isGroupTitle} size="m" title={groupItem?.title} />

		{#each groupItem.items as dropdownItem}
			<MenuItemList
				isGroupItem
				size="m"
				title={dropdownItem?.title}
				titleClasses={dropdownItem?.titleClasses}
				icon={dropdownItem?.icon}
				on:click={() => dropdownItem?.onClick(dropdownItem)}
			/>
		{/each}
	{/each}
</DropdownMenu>

<OptionManagmentModal
	bind:showModal={showModalMenu}
	topSectionTitle={dropdownMenuItemsData?.[0]?.title}
	itemsTop={dropdownMenuItemsData?.[0]?.items?.map((item) => {
		return {
			title: item?.title,
			titleDefault: item?.title,
			icon: mapIconSolidWeightToLight(item?.icon),
			disabled: false,
			onClick: item?.onClick,
		};
	})}
	bottomSectionTitle={dropdownMenuItemsData?.[1]?.title}
	itemsBottom={dropdownMenuItemsData?.[1]?.items?.map((item) => {
		return {
			title: item?.title,
			titleDefault: item?.title,
			icon: mapIconSolidWeightToLight(item?.icon),
			disabled: false,
			onClick: item?.onClick,
		};
	})}
/>

{#if showEditWidgetModal && $edit}
	<DashWidgetActionModal
		{routeParams}
		{dashboardId}
		bind:showModal={showEditWidgetModal}
		dashEditItem={$edit ? dashWidgetItemEdit : undefined}
		editColumn={dashWidgetItemEdit.col === 'right' ? 'right' : 'left'}
		{energyOnly}
	/>
{/if}

{#if showDeleteModal && $edit}
	<DashDeleteWidgetModal
		editItem={dashWidgetItemEdit}
		bind:showModal={showDeleteModal}
		on:delete-widget-finish
	/>
{/if}

{#if showItemReorderModal && $edit}
	<DashReorderWidgetItemsModal
		{dashboardId}
		editItem={dashWidgetItemEdit}
		bind:showModal={showItemReorderModal}
	/>
{/if}
